import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Form, Modal, Button, Table, Badge, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FiEdit2, FiExternalLink } from "react-icons/fi";

import {
  getAllPartners,
  getAllPartnerById,
  createPartner,
  updatePartnerColorById,
} from "../../api/axios";
import { showToast } from "../../Utills/toast";
import CmsCircularProgress from "../../Components/Common/CircularProgress";

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  password: "",
  confirmPassword: "",
  sub_domain: "",
  membership_prefix: "",
  group_namenum: "",
  is_white_labelled: false,
  send_confirmation_email: true,
  import_membership_auto_renew: true,
  import_membership_auto_renew_email: false,
  logo: null,
  logoPreview: "",
};

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function PartnerFormModal({ show, onHide, mode, partnerId, onSuccess }) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;

    (async () => {
      if (mode === "create") {
        setForm(emptyForm);
        return;
      }
      if (!partnerId) return;
      setLoading(true);
      try {
        const res = await getAllPartnerById(partnerId);
        const data = res?.data?.data;
        const row = Array.isArray(data) ? data[0] : data;
        if (!cancelled && row) {
          setForm({
            first_name: row.first_name || "",
            last_name: row.last_name || "",
            email: row.email || "",
            phone_number: row.phone_number || "",
            password: "",
            confirmPassword: "",
            sub_domain: row.sub_domain || "",
            membership_prefix: row.membership_prefix || "",
            group_namenum: row.group_namenum || "",
            is_white_labelled: Boolean(Number(row.is_white_labelled)),
            send_confirmation_email: row.send_confirmation_email === undefined
              ? true
              : Boolean(Number(row.send_confirmation_email)),
            import_membership_auto_renew:
              row.import_membership_auto_renew === undefined ||
              row.import_membership_auto_renew === null
                ? true
                : Boolean(Number(row.import_membership_auto_renew)),
            import_membership_auto_renew_email: Boolean(
              Number(row.import_membership_auto_renew_email),
            ),
            logo: null,
            logoPreview: row.logo || "",
          });
        }
      } catch (err) {
        showToast({
          type: "error",
          message: err?.response?.data?.message || "Failed to load partner.",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, mode, partnerId]);

  const setField = (key, value) =>
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "import_membership_auto_renew" && !value) {
        next.import_membership_auto_renew_email = false;
      }
      return next;
    });

  const validate = () => {
    if (!form.first_name.trim()) return "First name is required.";
    if (!form.last_name.trim()) return "Last name is required.";
    if (!form.email.trim()) return "Email is required.";
    if (!form.sub_domain.trim()) return "Subdomain is required.";
    if (!form.membership_prefix.trim()) return "Membership prefix is required.";
    if (!form.group_namenum.trim()) return "Group name is required.";
    if (mode === "create") {
      if (!form.password || form.password.length < 8) {
        return "Password must be at least 8 characters.";
      }
      if (form.password !== form.confirmPassword) return "Passwords do not match.";
      if (!form.logo) return "Logo is required.";
    } else if (form.password) {
      if (form.password.length < 8) return "Password must be at least 8 characters.";
      if (form.password !== form.confirmPassword) return "Passwords do not match.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      showToast({ type: "error", message: error });
      return;
    }

    const body = new FormData();
    body.append("first_name", form.first_name.trim());
    body.append("last_name", form.last_name.trim());
    body.append("email", form.email.trim());
    body.append("phone_number", form.phone_number || "");
    body.append("sub_domain", form.sub_domain.trim());
    body.append("membership_prefix", form.membership_prefix.trim());
    body.append("group_namenum", form.group_namenum.trim());
    body.append("is_white_labelled", form.is_white_labelled ? "true" : "false");
    body.append("send_confirmation_email", form.send_confirmation_email ? "1" : "0");
    body.append(
      "import_membership_auto_renew",
      form.import_membership_auto_renew ? "1" : "0",
    );
    body.append(
      "import_membership_auto_renew_email",
      form.import_membership_auto_renew && form.import_membership_auto_renew_email
        ? "1"
        : "0",
    );

    if (form.password) {
      body.append("password", form.password);
      body.append("confirmPassword", form.confirmPassword);
    }
    if (form.logo) {
      body.append("logo", form.logo);
    } else if (mode === "edit" && form.logoPreview) {
      body.append("logo", form.logoPreview);
    }

    setSaving(true);
    try {
      if (mode === "create") {
        await createPartner(body);
        showToast({ type: "success", message: "Partner created successfully." });
      } else {
        await updatePartnerColorById(partnerId, body);
        showToast({ type: "success", message: "Partner updated successfully." });
      }
      onSuccess();
      onHide();
    } catch (err) {
      showToast({
        type: "error",
        message: err?.response?.data?.message || "Save failed.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PartnerModal show={show} onHide={saving ? undefined : onHide} size="lg" centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{mode === "create" ? "Add partner" : "Edit partner"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <CmsCircularProgress />
          </div>
        ) : (
          <FormSections>
            <FormSection>
              <SectionTitle>Contact</SectionTitle>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>First name</Form.Label>
                  <Form.Control
                    value={form.first_name}
                    onChange={(e) => setField("first_name", e.target.value)}
                    disabled={saving}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Last name</Form.Label>
                  <Form.Control
                    value={form.last_name}
                    onChange={(e) => setField("last_name", e.target.value)}
                    disabled={saving}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    disabled={saving}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    value={form.phone_number}
                    onChange={(e) => setField("phone_number", e.target.value)}
                    disabled={saving}
                  />
                </Col>
              </Row>
            </FormSection>

            <FormSection>
              <SectionTitle>Partner setup</SectionTitle>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Subdomain</Form.Label>
                  <Form.Control
                    value={form.sub_domain}
                    onChange={(e) => setField("sub_domain", e.target.value)}
                    disabled={saving}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Membership prefix</Form.Label>
                  <Form.Control
                    value={form.membership_prefix}
                    onChange={(e) => setField("membership_prefix", e.target.value)}
                    disabled={saving}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Group name</Form.Label>
                  <Form.Control
                    value={form.group_namenum}
                    onChange={(e) => setField("group_namenum", e.target.value)}
                    disabled={saving}
                  />
                </Col>
              </Row>
            </FormSection>

            <FormSection>
              <SectionTitle>Branding</SectionTitle>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Label>Logo {mode === "create" ? "" : "(optional)"}</Form.Label>
                  <LogoRow>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      disabled={saving}
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setField("logo", file);
                        if (file) setField("logoPreview", URL.createObjectURL(file));
                      }}
                    />
                    {form.logoPreview ? (
                      <LogoPreview src={form.logoPreview} alt="logo preview" />
                    ) : (
                      <LogoPreviewPlaceholder>No logo</LogoPreviewPlaceholder>
                    )}
                  </LogoRow>
                </Col>
              </Row>
            </FormSection>

            <FormSection>
              <SectionTitle>Security</SectionTitle>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>
                    Password {mode === "edit" ? "(leave blank to keep)" : ""}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    disabled={saving}
                    autoComplete="new-password"
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Confirm password</Form.Label>
                  <Form.Control
                    type="password"
                    value={form.confirmPassword}
                    onChange={(e) => setField("confirmPassword", e.target.value)}
                    disabled={saving}
                    autoComplete="new-password"
                  />
                </Col>
              </Row>
            </FormSection>

            <FormSection $last>
              <SectionTitle>Settings</SectionTitle>
              <SettingsList>
                <SettingRow>
                  <SettingCopy>
                    <SettingLabel>White labelled</SettingLabel>
                    <SettingHelp>Use partner branding on white-label surfaces.</SettingHelp>
                  </SettingCopy>
                  <Form.Check
                    type="switch"
                    id="is-white-labelled"
                    checked={form.is_white_labelled}
                    disabled={saving}
                    onChange={(e) => setField("is_white_labelled", e.target.checked)}
                  />
                </SettingRow>
                <SettingRow>
                  <SettingCopy>
                    <SettingLabel>Send confirmation emails</SettingLabel>
                    <SettingHelp>Email members after successful eligibility registration.</SettingHelp>
                  </SettingCopy>
                  <Form.Check
                    type="switch"
                    id="send-confirmation-email"
                    checked={form.send_confirmation_email}
                    disabled={saving}
                    onChange={(e) => setField("send_confirmation_email", e.target.checked)}
                  />
                </SettingRow>
                <SettingRow>
                  <SettingCopy>
                    <SettingLabel>Auto-renew memberships from eligibility files</SettingLabel>
                    <SettingHelp>
                      Applies only to eligibility-imported users. Stripe memberships are not affected.
                    </SettingHelp>
                  </SettingCopy>
                  <Form.Check
                    type="switch"
                    id="import-membership-auto-renew"
                    checked={form.import_membership_auto_renew}
                    disabled={saving}
                    onChange={(e) =>
                      setField("import_membership_auto_renew", e.target.checked)
                    }
                  />
                </SettingRow>
                <SettingRow $last>
                  <SettingCopy>
                    <SettingLabel>Email member when membership auto-renews</SettingLabel>
                    <SettingHelp>
                      Requires auto-renew to be on. Off by default (silent renew).
                    </SettingHelp>
                  </SettingCopy>
                  <Form.Check
                    type="switch"
                    id="import-membership-auto-renew-email"
                    checked={
                      form.import_membership_auto_renew &&
                      form.import_membership_auto_renew_email
                    }
                    disabled={saving || !form.import_membership_auto_renew}
                    onChange={(e) =>
                      setField("import_membership_auto_renew_email", e.target.checked)
                    }
                  />
                </SettingRow>
              </SettingsList>
            </FormSection>
          </FormSections>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving || loading}>
          {saving ? "Saving…" : mode === "create" ? "Create" : "Save"}
        </Button>
      </Modal.Footer>
    </PartnerModal>
  );
}

export default function Partners() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState({ open: false, mode: "create", id: null });

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllPartners();
      setPartners(res?.data?.data || []);
    } catch (err) {
      showToast({
        type: "error",
        message: err?.response?.data?.message || "Failed to load partners.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const filtered = partners.filter((p) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      [p.first_name, p.last_name, p.email, p.sub_domain, String(p.id)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    const active = Number(p.status) === 1;
    const matchesStatus =
      statusFilter === ""
        ? true
        : statusFilter === "active"
          ? active
          : !active;
    return matchesSearch && matchesStatus;
  });

  return (
    <PageWrap>
      <HeaderRow>
        <div>
          <h2>Partners</h2>
          <p>Create and edit CMS partners.</p>
        </div>
        <HeaderActions>
          <Button variant="outline-secondary" size="sm" onClick={loadPartners} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setModal({ open: true, mode: "create", id: null })}
          >
            Add Partner
          </Button>
        </HeaderActions>
      </HeaderRow>

      <FilterBar>
        <FilterField>
          <Form.Label>Search</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Name, email, subdomain…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </FilterField>
        <FilterField $narrow>
          <Form.Label>Status</Form.Label>
          <Form.Select
            size="sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>
        </FilterField>
      </FilterBar>

      <Card>
        {loading ? (
          <div className="text-center py-5">
            <CmsCircularProgress />
          </div>
        ) : filtered.length === 0 ? (
          <Empty>No partners found.</Empty>
        ) : (
          <StyledTable responsive hover className="mb-0">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Subdomain</th>
                <th>Email</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const active = Number(row.status) === 1;
                return (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>
                      {[row.first_name, row.last_name].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td>{row.sub_domain || "—"}</td>
                    <td>{row.email || "—"}</td>
                    <td>
                      <Badge bg={active ? "success" : "secondary"}>
                        {active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="text-nowrap">{formatDate(row.created_at)}</td>
                    <td className="text-nowrap">
                      <ActionsCell>
                        <IconButton
                          size="sm"
                          variant="outline-secondary"
                          title="Edit partner"
                          aria-label="Edit partner"
                          onClick={() =>
                            setModal({ open: true, mode: "edit", id: row.id })
                          }
                        >
                          <FiEdit2 size={15} />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="outline-primary"
                          title="Open partner"
                          aria-label="Open partner"
                          onClick={() => navigate(`/${row.id}/general-settings`)}
                        >
                          <FiExternalLink size={15} />
                        </IconButton>
                      </ActionsCell>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        )}
      </Card>

      <PartnerFormModal
        show={modal.open}
        mode={modal.mode}
        partnerId={modal.id}
        onHide={() => setModal({ open: false, mode: "create", id: null })}
        onSuccess={loadPartners}
      />
    </PageWrap>
  );
}

const buttonReset = `
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto !important;
  margin: 0 !important;
  padding: 0.35rem 0.85rem !important;
  border-radius: 6px !important;
`;

const PageWrap = styled.div`
  padding: 20px 24px 28px;
  width: 100%;
  max-width: none;
  box-sizing: border-box;

  button {
    ${buttonReset}
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;

  h2 {
    margin: 0 0 4px;
    font-size: 1.45rem;
    font-weight: 600;
    color: #111827;
  }

  p {
    margin: 0;
    color: #6b7280;
    font-size: 0.925rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: flex-end;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 14px;
`;

const FilterField = styled.div`
  flex: ${(p) => (p.$narrow ? "0 0 180px" : "1 1 280px")};
  max-width: ${(p) => (p.$narrow ? "220px" : "480px")};

  label {
    display: block;
    margin-bottom: 4px;
    font-size: 0.8rem;
    font-weight: 500;
    color: #374151;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`;

const StyledTable = styled(Table)`
  margin-bottom: 0;
  width: 100%;

  thead th {
    background: #f3f4f6;
    color: #374151;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
    padding: 10px 14px;
  }

  tbody td {
    padding: 12px 14px;
    vertical-align: middle;
    font-size: 0.9rem;
    border-color: #f3f4f6;
  }
`;

const Empty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: #6b7280;
`;

const ActionsCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const IconButton = styled(Button)`
  ${buttonReset}
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
  border-radius: 8px !important;
`;

const LogoPreview = styled.img`
  display: block;
  max-height: 56px;
  max-width: 88px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px;
  background: #fff;
  flex: 0 0 88px;
  width: 88px;
  height: 56px;
`;

const PartnerModal = styled(Modal)`
  .modal-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;

    > * {
      margin: 0 !important;
    }

    .btn {
      width: auto !important;
      min-width: 88px;
      padding: 0.4rem 1rem !important;
    }
  }

  .modal-body {
    max-height: min(70vh, 640px);
    overflow-y: auto;
  }

  .form-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 4px;
  }
`;

const FormSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormSection = styled.section`
  padding-bottom: ${(p) => (p.$last ? "0" : "18px")};
  border-bottom: ${(p) => (p.$last ? "none" : "1px solid #f3f4f6")};
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .form-control {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

const LogoPreviewPlaceholder = styled.div`
  flex: 0 0 88px;
  width: 88px;
  height: 56px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 0.75rem;
`;

const SettingsList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`;

const SettingRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-bottom: ${(p) => (p.$last ? "none" : "1px solid #f3f4f6")};

  .form-check {
    margin: 2px 0 0;
    min-height: auto;
    padding-left: 2.4em;
  }

  .form-check-input {
    margin-top: 0.15rem;
  }
`;

const SettingCopy = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

const SettingLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
`;

const SettingHelp = styled.div`
  margin-top: 2px;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.35;
`;
